// Products › Services › Combination rules (UC-CATALOGUE-010; no prototype screen — designed from the use case): which services
// exclude or require each other, how many an order may hold, how often a patient may have one. Stored now; an order's
// Consultation section checks them from Sprint 5 (R-20). Owner and Admin only, reading included (`catalogue.rules.write`).
// Rules are deactivated and reactivated under their version, never deleted.
import { CATALOGUE_LIMITS, SERVICE_COMBINATION_RULE_TYPES, SERVICE_COMBINATION_SEVERITIES, type ProductSummary, type ServiceCombinationMemberRole, type ServiceCombinationRuleType, type ServiceCombinationRuleView, type ServiceCombinationSeverity } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { serviceRules, type RuleBody } from "../api/endpoints.catalogue";
import { carriedOut, isApiError } from "../api/problem";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Chip, Empty, ErrorAlert, Field, Loading, Modal, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { MEMBER_ROLE_LABEL, ProductPicker, RULE_TYPE_LABEL, SEVERITY_LABEL, refusalWords } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";

const etagOf = (r: { version: number }): string => `W/"${r.version}"`;
const hasParameter = (t: ServiceCombinationRuleType): boolean => t === "MaxQuantityPerOrder" || t === "OncePerPatientPerNDays";
const parameterText = (r: ServiceCombinationRuleView): string => (r.ruleType === "MaxQuantityPerOrder" ? `at most ${r.parameter} per order` : r.ruleType === "OncePerPatientPerNDays" ? `once per ${r.parameter} days` : "—");

export function ServiceRules() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const title = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const list = useQuery({ queryKey: ["service-rules"], queryFn: () => serviceRules.list(), enabled: can(s, "catalogue.rules.write") });
  const [editing, setEditing] = useState<ServiceCombinationRuleView | "new" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A toggle's refusal, with the rule it was about; a conflict reads the rules again so the next toggle is under the new version.
  const [error, setError] = useState<{ err: unknown; label: string } | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["service-rules"] }); };
  if (!can(s, "catalogue.rules.write")) {
    return <section className="screen" id="s-service-rules"><div className="content"><PageHead title="Combination rules" /><NoAccessPanel page="Combination rules" permission="catalogue.rules.write" /></div></section>;
  }
  const toggle = async (r: ServiceCombinationRuleView) => {
    if (toggling !== null) return;
    setError(null);
    setToggling(r.id);
    try {
      await serviceRules.patch(r.id, { isActive: !r.isActive }, etagOf(r));
      setNotice(`${r.name} ${r.isActive ? "deactivated — it will check nothing" : "reactivated"}.`);
      refresh();
    } catch (err) {
      setNotice(null);
      setError({ err, label: r.name });
      if (isApiError(err) && err.code === "VERSION_CONFLICT") refresh();
    } finally { setToggling(null); }
  };
  const rows = list.data?.data.items ?? [];
  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  return (
    <section className="screen" id="s-service-rules">
      <div className="content">
        <PageHead title="Combination rules" sub="Which services exclude or require each other on an order · stored now, checked on orders from Sprint 5"
          actions={<><Link className="btn" to="/products?tab=services">Services</Link><button className="btn primary" type="button" disabled={!online} title={title} onClick={() => setEditing("new")}><Icon name="plus" />Add rule</button></>} />
        {notice && <Alert tone="ok" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}><b>{notice}</b></Alert>}
        {error !== null && (isApiError(error.err) && error.err.code === "VERSION_CONFLICT"
          ? <Alert tone="warn" role="alert"><b>{error.label} was changed since the list was read; it has been read again.</b> Check it, then try again.</Alert>
          : <Alert tone="crit" role="alert"><b>Not changed.</b> {refusalWords(error.err)}</Alert>)}
        {list.isPending && <Loading what="the rules" />}
        {list.isError && (denied ? <NoAccessPanel page="Combination rules" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel tight foot={<span className="help">Block: the order cannot be saved. Warn: it is saved with a reason. An inactive rule is kept and checks nothing.</span>}>
            {rows.length === 0 ? <Empty title="No combination rules yet.">Orders accept any services together until you add one.</Empty> : (
              <div className="tbl-scroll"><table>
                <thead><tr><th>Rule</th><th>Type</th><th>Services</th><th>Limit</th><th>Severity</th><th>Message</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="strong">{r.name}</td>
                      <td>{RULE_TYPE_LABEL[r.ruleType]}</td>
                      <td>{r.members.map((m) => <div key={m.productId} className="nw"><span className="mono">{m.sku}</span> {m.name}{r.ruleType === "Requires" ? <span className="faint"> · {MEMBER_ROLE_LABEL[m.role].toLowerCase()}</span> : null}</div>)}</td>
                      <td className="nw">{parameterText(r)}</td>
                      <td>{SEVERITY_LABEL[r.severity]}</td>
                      <td>{r.message}</td>
                      <td>{r.isActive ? <Chip tone="ok" dot>Active</Chip> : <Chip tone="neutral">Inactive</Chip>}</td>
                      <td className="r nw">
                        <button className="btn sm" type="button" disabled={!online} title={title} onClick={() => setEditing(r)}>Edit</button>{" "}
                        <button className="btn sm" type="button" disabled={!online || toggling !== null} title={title} onClick={() => void toggle(r)}>{toggling === r.id ? (r.isActive ? "Deactivating…" : "Reactivating…") : r.isActive ? "Deactivate" : "Reactivate"}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </Panel>
        )}
        {editing !== null && <RuleModal current={editing === "new" ? null : editing} onClose={() => setEditing(null)} onConflict={refresh} onSaved={(r, created) => { setEditing(null); setNotice(`${r.name} ${created ? "added" : "saved"}.`); refresh(); }}
          onCarriedOut={(words) => { setEditing(null); setNotice(words); refresh(); }} />}
      </div>
    </section>
  );
}

interface MemberRow { key: string; product: ProductSummary | null; role: ServiceCombinationMemberRole }
const RULE_FIELDS = new Set(["name", "ruleType", "parameter", "severity", "message", "members", "isActive"]);

function RuleModal({ current, onClose, onSaved, onCarriedOut, onConflict }: { current: ServiceCombinationRuleView | null; onClose: () => void; onSaved: (r: ServiceCombinationRuleView, created: boolean) => void; onCarriedOut: (words: string) => void; onConflict: () => void }) {
  const [name, setName] = useState(current?.name ?? "");
  const [ruleType, setRuleType] = useState<ServiceCombinationRuleType>(current?.ruleType ?? "MutuallyExclusive");
  const [parameter, setParameter] = useState(current?.parameter !== null && current?.parameter !== undefined ? String(current.parameter) : "");
  const [severity, setSeverity] = useState<ServiceCombinationSeverity>(current?.severity ?? "Block");
  const [message, setMessage] = useState(current?.message ?? "");
  const [members, setMembers] = useState<MemberRow[]>(() => current
    ? current.members.map((m, i) => ({ key: `m${i}`, role: m.role, product: { id: m.productId, sku: m.sku, displayName: m.name, name: m.name, status: m.status } as ProductSummary }))
    : [{ key: "m0", product: null, role: "Member" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<Record<string, string>>({});
  const idem = useMemo(() => newIdempotencyKey(), []);
  const requires = ruleType === "Requires";
  const err = (path: string): string | undefined => local[path] ?? (isApiError(error) ? error.field(path)?.message : undefined);
  const unplaced = isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors.filter((f) => !RULE_FIELDS.has(f.path) && !/^members\.\d+\.(productId|role)$/.test(f.path)) : [];
  const conflict = isApiError(error) && error.code === "VERSION_CONFLICT";
  // Every refusal no field holds is said above the fields, in words (D2-3): a 500, a 403, a 429, a reused key.
  const general = error !== null && !conflict && !(isApiError(error) && error.code === "NOT_FOUND") && !(isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 && unplaced.length === 0);
  const membersBody = () => members.filter((m) => m.product).map((m) => (requires ? { productId: m.product!.id, role: m.role === "Member" ? "Trigger" : m.role } : { productId: m.product!.id }));
  const submit = async () => {
    if (busy) return;
    const issues: Record<string, string> = {};
    if (!name.trim()) issues.name = "Name the rule";
    if (!message.trim()) issues.message = "Enter the message an order shows";
    if (hasParameter(ruleType) && !/^\d+$/.test(parameter.trim())) issues.parameter = ruleType === "MaxQuantityPerOrder" ? "1 to 99" : "1 to 3 650 days";
    if (members.every((m) => !m.product)) issues.members = "Choose at least one service";
    setLocal(issues);
    if (Object.keys(issues).length > 0) return;
    const param = hasParameter(ruleType) ? Number(parameter.trim()) : null;
    setBusy(true);
    setError(null);
    try {
      if (current) {
        const body: RuleBody = {};
        if (name.trim() !== current.name) body.name = name.trim();
        if (ruleType !== current.ruleType) body.ruleType = ruleType;
        if (param !== current.parameter) body.parameter = param;
        if (severity !== current.severity) body.severity = severity;
        if (message.trim() !== current.message) body.message = message.trim();
        const was = current.members.map((m) => (current.ruleType === "Requires" ? { productId: m.productId, role: m.role } : { productId: m.productId }));
        if (JSON.stringify(membersBody()) !== JSON.stringify(was) || ruleType !== current.ruleType) body.members = membersBody();
        if (Object.keys(body).length === 0) { onClose(); return; }
        const { data } = await serviceRules.patch(current.id, body, etagOf(current));
        onSaved(data, false);
      } else {
        const body: RuleBody = { name: name.trim(), ruleType, severity, message: message.trim(), members: membersBody() };
        if (param !== null) body.parameter = param;
        const { data } = await serviceRules.create(body, idem);
        onSaved(data, true);
      }
    } catch (e) {
      // Add rule pressed again after a lost answer, under the dialog's one key, answered as carried out — added once, its answer not
      // kept: the rules, read again, list it (UC-CATALOGUE-010 A6; BR-GOVERNANCE-016).
      if (!current && carriedOut(e)) { onCarriedOut(`${name.trim()} added: its answer did not arrive, so it is listed here.`); return; }
      setError(e);
      if (isApiError(e) && e.code === "VERSION_CONFLICT") onConflict();
    }
    finally { setBusy(false); }
  };
  return (
    <Modal title={current ? `Edit ${current.name}` : "Add a combination rule"} meta="checked on orders from Sprint 5" wide onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : current ? "Save changes" : "Add rule"}</button>
    </>}>
      {conflict && <Alert tone="warn" role="alert"><b>Someone changed this rule since the list was read.</b> The list has been read again — close this and open the rule again to see the change.</Alert>}
      {isApiError(error) && error.code === "NOT_FOUND" && <Alert tone="crit" role="alert"><b>Not saved.</b> A service was not found — remove it and choose again.</Alert>}
      {general && <Alert tone="crit" role="alert"><b>Not saved.</b> {unplaced.length > 0 ? unplaced.map((f) => f.message).join(" · ") : refusalWords(error)}</Alert>}
      <div className="grid-2">
        <Field label="Name" required htmlFor="rule-name" error={err("name")}><TextInput id="rule-name" maxLength={CATALOGUE_LIMITS.ruleNameMax} value={name} onChange={(e) => setName(e.target.value)} error={!!err("name")} /></Field>
        <Field label="Type" required htmlFor="rule-type" error={err("ruleType")}>
          <Select id="rule-type" value={ruleType} onChange={(e) => setRuleType(e.target.value as ServiceCombinationRuleType)}>
            {SERVICE_COMBINATION_RULE_TYPES.map((t) => <option key={t} value={t}>{RULE_TYPE_LABEL[t]}</option>)}
          </Select>
        </Field>
        {hasParameter(ruleType) && (
          <Field label={ruleType === "MaxQuantityPerOrder" ? "Most per order" : "Days between"} required htmlFor="rule-param" error={err("parameter")} help={ruleType === "MaxQuantityPerOrder" ? "1 to 99" : "1 to 3 650 days"}>
            <TextInput id="rule-param" mono inputMode="numeric" value={parameter} onChange={(e) => setParameter(e.target.value)} error={!!err("parameter")} />
          </Field>
        )}
        <Field label="Severity" required htmlFor="rule-severity" error={err("severity")} help={severity === "Block" ? "The order cannot be saved" : "The order is saved with a reason"}>
          <Select id="rule-severity" value={severity} onChange={(e) => setSeverity(e.target.value as ServiceCombinationSeverity)}>
            {SERVICE_COMBINATION_SEVERITIES.map((v) => <option key={v} value={v}>{SEVERITY_LABEL[v]}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Message" required htmlFor="rule-message" error={err("message")} help="What the order shows, naming the codes"><TextInput id="rule-message" maxLength={CATALOGUE_LIMITS.ruleMessageMax} value={message} onChange={(e) => setMessage(e.target.value)} error={!!err("message")} /></Field>
      <div className="stack" style={{ gap: 8 }}>
        <div className="small strong muted">Services</div>
        {err("members") && <div className="error" role="alert">{err("members")}</div>}
        {members.map((m, i) => (
          <div key={m.key} className="row">
            <div className="grow">
              <ProductPicker id={`rule-member-${i}`} label={`Service ${i + 1}`} value={m.product} categories={["Service"]} keyTag="rule-member"
                allow={(p) => !members.some((o, j) => j !== i && o.product?.id === p.id)} error={err(`members.${i}.productId`)}
                onPick={(p) => setMembers((ms) => ms.map((x, j) => (j === i ? { ...x, product: p } : x)))} />
            </div>
            {requires && (
              <Field label="Role" htmlFor={`rule-role-${i}`} error={err(`members.${i}.role`)}>
                <Select id={`rule-role-${i}`} value={m.role === "Member" ? "Trigger" : m.role} onChange={(e) => setMembers((ms) => ms.map((x, j) => (j === i ? { ...x, role: e.target.value as ServiceCombinationMemberRole } : x)))}>
                  <option value="Trigger">{MEMBER_ROLE_LABEL.Trigger}</option>
                  <option value="Required">{MEMBER_ROLE_LABEL.Required}</option>
                </Select>
              </Field>
            )}
            <button className="btn sm" type="button" aria-label={`Remove service ${i + 1}`} disabled={members.length === 1} onClick={() => setMembers((ms) => ms.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <div><button className="btn sm" type="button" disabled={members.length >= CATALOGUE_LIMITS.ruleMembersMax} onClick={() => setMembers((ms) => [...ms, { key: newIdempotencyKey(), product: null, role: requires ? "Required" : "Member" }])}><Icon name="plus" />Add service</button></div>
      </div>
    </Modal>
  );
}
